<template lang="pug">
.purchase-invoice-detail(v-loading="loading")

  section(v-if="invoice")
    el-header.page-header
      el-page-header(@back="back" :content="title")
      .actions
        el-button(v-if="invoice.disregard" icon="el-icon-refresh-left" :loading="disregarding" @click="reconsider")
          | Voltar a considerar
        template(v-else)
          el-button(v-if="pendingItems.length" icon="el-icon-close" :loading="ignoring" @click="ignoreInvoice")
            | Não é compra para revenda
          el-button(type="danger" plain icon="el-icon-remove-outline" :loading="disregarding" @click="disregard")
            | Desconsiderar nota

    el-alert.disregarded(v-if="invoice.disregard" type="warning" :closable="false" show-icon
      :title="`Nota desconsiderada em ${formatDateTime(invoice.disregard.disregardedAt)}`"
      :description="invoice.disregard.reason")

    el-descriptions(:column="3" border size="small")
      el-descriptions-item(label="Chave de acesso" :span="3") {{ invoice.accessKey }}
      el-descriptions-item(label="Número / Série") {{ invoice.number }} / {{ invoice.series }}
      el-descriptions-item(label="Emissão") {{ formatDate(invoice.issuedAt) }}
      el-descriptions-item(label="Situação")
        el-tag(size="mini" :type="invoice.status === 'CANCELLED' ? 'danger' : 'success'") {{ statusLabels[invoice.status] }}
      el-descriptions-item(label="Fornecedor") {{ invoice.issuerName }}
      el-descriptions-item(label="CNPJ") {{ formatDocument(invoice.issuerDocument) }}
      el-descriptions-item(label="Regime tributário") {{ taxRegimeLabels[invoice.taxRegimeCode] || invoice.taxRegimeCode }}
      el-descriptions-item(label="Natureza da operação") {{ invoice.operationNature }}
      el-descriptions-item(label="Finalidade") {{ purposeLabels[invoice.purpose] }}
      el-descriptions-item(label="Manifestação") {{ manifestationLabels[invoice.recipientManifestation] || '-' }}
      el-descriptions-item(v-if="invoice.referencedAccessKeys.length" label="Notas referenciadas" :span="3")
        div(v-for="key in invoice.referencedAccessKeys" :key="key") {{ key }}

    h4 Totais
    el-descriptions(:column="5" border size="small")
      el-descriptions-item(label="Produtos") {{ formatCurrency(invoice.totals.productsAmount) }}
      el-descriptions-item(label="Desconto") {{ formatCurrency(invoice.totals.discountAmount) }}
      el-descriptions-item(label="Frete") {{ formatCurrency(invoice.totals.freightAmount) }}
      el-descriptions-item(label="Seguro") {{ formatCurrency(invoice.totals.insuranceAmount) }}
      el-descriptions-item(label="Outras despesas") {{ formatCurrency(invoice.totals.otherExpensesAmount) }}
      el-descriptions-item(label="IPI") {{ formatCurrency(invoice.totals.ipiAmount) }}
      el-descriptions-item(label="ICMS") {{ formatCurrency(invoice.totals.icmsAmount) }}
      el-descriptions-item(label="ICMS desonerado") {{ formatCurrency(invoice.totals.icmsDesoneradoAmount) }}
      el-descriptions-item(label="ICMS ST") {{ formatCurrency(invoice.totals.icmsStAmount) }}
      el-descriptions-item(label="Total da nota") {{ formatCurrency(invoice.totals.totalAmount) }}

    h4 Itens
    el-table(:data="items" size="mini" row-key="id")
      el-table-column(type="expand")
        template(slot-scope="scope")
          el-table(:data="scope.row.taxes" size="mini")
            el-table-column(label="Tributo" width="180")
              template(slot-scope="tax") {{ taxLabels[tax.row.tax] }}
            el-table-column(prop="situationCode" label="CST/CSOSN" width="100")
            el-table-column(label="Base" align="right")
              template(slot-scope="tax") {{ formatNumber(tax.row.base) }}
            el-table-column(label="Redução da base (%)" align="right")
              template(slot-scope="tax") {{ formatNumber(tax.row.baseReductionRate) }}
            el-table-column(label="Alíquota" align="right")
              template(slot-scope="tax") {{ formatNumber(tax.row.rate) }}
            el-table-column(label="Valor" align="right")
              template(slot-scope="tax") {{ formatCurrency(tax.row.amount) }}
            el-table-column(label="Deduzido do total" width="140")
              template(slot-scope="tax") {{ deductedLabel(tax.row.deductedFromTotal) }}
          p.cost(v-if="costsByItem[scope.row.id]")
            | Custo bruto {{ formatCurrency(costsByItem[scope.row.id].grossCost) }} ·
            | crédito de ICMS {{ formatCurrency(costsByItem[scope.row.id].icmsCredit) }} ·
            | base PIS/COFINS {{ formatCurrency(costsByItem[scope.row.id].pisCofinsBase) }}
            | a {{ formatNumber(costsByItem[scope.row.id].pisCofinsRate) }}%:
            | PIS {{ formatCurrency(costsByItem[scope.row.id].pisCredit) }},
            | COFINS {{ formatCurrency(costsByItem[scope.row.id].cofinsCredit) }} ·
            | custo líquido {{ formatCurrency(costsByItem[scope.row.id].netCost) }}
      el-table-column(prop="itemNumber" label="#" width="50")
      el-table-column(prop="supplierCode" label="Código" width="110" show-overflow-tooltip)
      el-table-column(prop="description" label="Descrição" min-width="220" show-overflow-tooltip)
      el-table-column(prop="ean" label="EAN" width="130")
      el-table-column(prop="ncm" label="NCM" width="90")
      el-table-column(prop="cfop" label="CFOP" width="70")
      el-table-column(label="Classificação" width="170")
        template(slot-scope="scope")
          el-tag(size="mini" :type="scope.row.classification === 'REVIEW' ? 'warning' : 'info'") {{ classificationLabels[scope.row.classification] }}
      el-table-column(label="Quantidade" width="110" align="right")
        template(slot-scope="scope") {{ formatNumber(scope.row.quantity) }} {{ scope.row.unit }}
      el-table-column(label="Valor bruto" width="120" align="right")
        template(slot-scope="scope") {{ formatCurrency(scope.row.grossAmount) }}
      el-table-column(label="Desconto" width="110" align="right")
        template(slot-scope="scope") {{ formatCurrency(scope.row.discountAmount) }}
      el-table-column(label="Frete" width="100" align="right")
        template(slot-scope="scope") {{ formatCurrency(scope.row.freightAmount) }}
      el-table-column(label="ICMS" width="110" align="right")
        template(slot-scope="scope") {{ formatCurrency(taxAmount(scope.row, 'ICMS')) }}
      el-table-column(label="IPI" width="100" align="right")
        template(slot-scope="scope") {{ formatCurrency(taxAmount(scope.row, 'IPI')) }}
      el-table-column(label="Créditos" width="110" align="right")
        template(slot-scope="scope")
          template(v-if="costsByItem[scope.row.id]") {{ formatCurrency(costsByItem[scope.row.id].credits) }}
      el-table-column(label="Custo líquido" width="120" align="right")
        template(slot-scope="scope")
          template(v-if="costsByItem[scope.row.id]") {{ formatCurrency(costsByItem[scope.row.id].netCost) }}
      el-table-column(label="Produto" width="160")
        template(slot-scope="scope")
          template(v-if="scope.row.matchStatus === 'MATCHED'")
            router-link(:to="{ name: 'product-detail', params: { id: scope.row.productId } }") Produto {{ scope.row.productId }}
            span(v-if="Number(scope.row.unitsPerSupplierUnit) > 1")  × {{ Number(scope.row.unitsPerSupplierUnit) }}
          el-tag(v-else-if="scope.row.needsProduct" size="mini" :type="scope.row.matchStatus === 'PENDING' ? 'warning' : 'info'")
            | {{ matchStatusLabels[scope.row.matchStatus] }}
      el-table-column(v-if="!invoice.disregard" width="130" align="center" fixed="right")
        template(slot-scope="scope")
          template(v-if="scope.row.needsProduct && scope.row.matchStatus === 'PENDING'")
            el-tooltip(content="Vincular a um produto" placement="top")
              el-button(size="mini" type="primary" plain icon="el-icon-link" @click="openReview(scope.row)")
            el-tooltip(content="Desconsiderar item" placement="top")
              el-button(size="mini" plain icon="el-icon-remove-outline" @click="disregardItem(scope.row)")
          el-tooltip(v-else-if="scope.row.matchStatus !== 'PENDING'" content="Desfazer" placement="top")
            el-button(size="mini" plain icon="el-icon-refresh-left" @click="unmatch(scope.row)")

</template>
<script setup>
import { computed, getCurrentInstance, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue2-helpers/vue-router'

import PurchaseInvoiceItemService from '@/services/fiscal/PurchaseInvoiceItemService'
import PurchaseInvoiceService from '@/services/fiscal/PurchaseInvoiceService'
import { useMessage } from '@/utils/Message'
import {
  classificationLabels, formatCurrency, formatDate, formatDateTime, formatDocument, formatNumber, manifestationLabels,
  matchStatusLabels, purposeLabels, statusLabels, taxLabels, taxRegimeLabels,
} from './labels'

const route = useRoute()
const router = useRouter()
const message = useMessage()
const confirm = getCurrentInstance().proxy.$confirm
const prompt = getCurrentInstance().proxy.$prompt

const loading = ref(true)
const ignoring = ref(false)
const disregarding = ref(false)
const detail = ref(null)

const invoice = computed(() => (detail.value ? detail.value.invoice : null))
const items = computed(() => (detail.value ? detail.value.items : []))
const pendingItems = computed(() => items.value.filter(it => it.needsProduct && it.matchStatus === 'PENDING'))
const costsByItem = computed(() => Object.fromEntries(
  (detail.value ? detail.value.costs : []).map(cost => [cost.purchaseInvoiceItemId, cost]),
))
const title = computed(() => (invoice.value ? `NF-e ${invoice.value.number} · ${invoice.value.issuerName}` : ''))

const back = () => router.push({ name: 'purchase-invoice-list' })

const taxAmount = (item, tax) => {
  const line = item.taxes.find(it => it.tax === tax)
  return line ? line.amount : null
}

const deductedLabel = (deducted) => {
  if (deducted === null || deducted === undefined) return ''
  return deducted ? 'Sim' : 'Não'
}

const load = async () => {
  try {
    detail.value = await PurchaseInvoiceService.find(route.params.id)
  } catch (error) {
    message.error(`Não foi possível carregar a nota: ${error.description || error.message}`)
  } finally {
    loading.value = false
  }
}

const ignoreInvoice = async () => {
  try {
    await confirm(
      `Marcar os ${pendingItems.value.length} itens sem produto desta nota como não sendo produtos de estoque? `
      + 'Use para notas que não são de compra para revenda, como material de escritório ou equipamentos da equipe. '
      + 'Os códigos deste fornecedor serão lembrados para as próximas notas.',
    )
  } catch (cancelled) {
    return
  }

  ignoring.value = true

  try {
    const result = await PurchaseInvoiceService.ignoreItems(invoice.value.id, true)
    message.success(`${result.ignored} itens marcados como não sendo produtos de estoque.`)
    await load()
  } catch (error) {
    message.error(error.description || error.message)
  } finally {
    ignoring.value = false
  }
}

const disregard = async () => {
  let reason

  try {
    reason = (await prompt(
      'A nota sai da conciliação e não conta em nenhum mês. Nada é aprendido com os itens dela, então os produtos '
      + 'continuam podendo ser vinculados em outras notas. Por que desconsiderar esta nota?',
      'Desconsiderar nota',
      {
        confirmButtonText: 'Desconsiderar',
        cancelButtonText: 'Cancelar',
        inputPlaceholder: 'Ex.: emitida por engano, devolvida pela NF 11043',
        inputValidator: value => Boolean(value && value.trim()) || 'Informe o motivo',
      },
    )).value
  } catch (cancelled) {
    return
  }

  disregarding.value = true

  try {
    await PurchaseInvoiceService.disregard(invoice.value.id, reason.trim())
    message.success('Nota desconsiderada.')
    await load()
  } catch (error) {
    message.error(error.description || error.message)
  } finally {
    disregarding.value = false
  }
}

const reconsider = async () => {
  try {
    await confirm('Voltar a considerar esta nota? Os itens dela voltam para a conciliação e ela volta a contar no mês do pedido vinculado.')
  } catch (cancelled) {
    return
  }

  disregarding.value = true

  try {
    await PurchaseInvoiceService.reconsider(invoice.value.id)
    message.success('A nota voltou a ser considerada.')
    await load()
  } catch (error) {
    message.error(error.description || error.message)
  } finally {
    disregarding.value = false
  }
}

const openReview = item => router.push({ name: 'purchase-invoice-item-review', query: { search: item.supplierCode } })

const disregardItem = async (item) => {
  try {
    await confirm(
      `Desconsiderar o item ${item.itemNumber} (${item.description})? Ele não conta em nenhum mês e nada é lembrado `
      + 'sobre o código do fornecedor, então o produto continua podendo ser vinculado em outras notas.',
    )
  } catch (cancelled) {
    return
  }

  try {
    await PurchaseInvoiceItemService.disregard(item.id)
    message.success(`Item ${item.itemNumber} desconsiderado.`)
    await load()
  } catch (error) {
    message.error(error.description || error.message)
  }
}

const unmatch = async (item) => {
  try {
    await confirm(`Desfazer a decisão sobre o item ${item.itemNumber}? Ele volta para a lista de itens sem produto.`)
  } catch (cancelled) {
    return
  }

  try {
    await PurchaseInvoiceItemService.unmatch(item.id, false)
    message.success('Vínculo desfeito.')
    await load()
  } catch (error) {
    message.error(error.description || error.message)
  }
}

onMounted(load)
</script>
<style lang="scss" scoped>
h4 {
  margin: 20px 0 8px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  .actions {
    display: flex;
    flex-shrink: 0;
    gap: 8px;
  }

  // The gap above spaces the buttons; Element's own margin between adjacent buttons would double it
  .actions .el-button + .el-button {
    margin-left: 0;
  }
}

.cost {
  margin: 8px 0 0;
  color: #5e7382;
}

.disregarded {
  margin-bottom: 16px;
}
</style>
